import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of, map, startWith, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { FetchPriorityEnum } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { finShare, safeMap, snapshot } from '@fcom/rx';
import { isBlank } from '@fcom/core/utils/utils';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import * as GlobalBookingActions from '@fcom/common/store/actions/global-booking.actions';
import { AppState, CMSDiscountCode, GlobalBookingTravelClass } from '@fcom/core';

import { BlockOfferData, BlockOffer } from '../../interfaces/destination-promo.interface';
import { MarketingOffer } from '../../interfaces/marketing-offer.interface';
import { CheapestPriceForAllDestinationsService } from '../../services/cheapest-price/cheapest-price-for-all-destinations.service';

@Component({
  selector: 'fin-block-offer',
  templateUrl: './block-offer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BlockOfferComponent implements OnInit {
  private languageService = inject(LanguageService);
  private store$ = inject<Store<AppState>>(Store);
  private gtmService = inject(GtmService);
  private cheapestPriceForAllDestinationsService = inject(CheapestPriceForAllDestinationsService);

  @Output()
  destinationClicked: EventEmitter<[BlockOffer, GlobalBookingTravelClass]> = new EventEmitter();
  @Output()
  flightHotelClicked: EventEmitter<{ amLink: string; destinationTitle: string; destinationCode: string }> =
    new EventEmitter();
  @Output()
  calendarClicked: EventEmitter<[Event, BlockOffer, GlobalBookingTravelClass, TripType]> = new EventEmitter();

  private readonly tripTypeSubject$ = new BehaviorSubject<TripType>(TripType.RETURN);
  private readonly offerSubject$ = new BehaviorSubject<BlockOfferData | null>(null);

  @Input()
  set tripType(value: TripType) {
    this.tripTypeSubject$.next(value ?? TripType.RETURN);
  }
  get tripType(): TripType {
    return this.tripTypeSubject$.value;
  }

  @Input()
  set offer(value: BlockOfferData) {
    this.offerSubject$.next(value ?? null);
  }
  get offer(): BlockOfferData {
    return this.offerSubject$.value as BlockOfferData;
  }

  @Input()
  fetchImagePriority?: FetchPriorityEnum;

  @Input()
  showPriceCalendarButton = true;

  blockOffer$: Observable<BlockOffer>;

  ngOnInit(): void {
    const countryName$ = this.offerSubject$.pipe(
      switchMap((offer) => (offer ? this.countryName(offer) : of(undefined))),
      finShare()
    );

    this.blockOffer$ = combineLatest([this.offerSubject$, this.tripTypeSubject$, countryName$]).pipe(
      switchMap(([offer, tripType, countryName]) => {
        if (!offer) {
          return of({} as BlockOffer);
        }

        return this.marketingOffer(offer.destination, tripType).pipe(
          map((marketingOffer) => ({
            ...this.setUpPrices(offer, marketingOffer, tripType),
            countryName,
            marketingOffer,
          }))
        );
      })
    );
  }

  emitCalendarClick(
    [event, travelClass, tripType]: [Event, GlobalBookingTravelClass, TripType],
    blockOffer: BlockOffer
  ): void {
    this.calendarClicked.emit([event, blockOffer, travelClass, tripType]);
  }

  private countryName({ flag }: BlockOfferData): Observable<string> {
    if (isBlank(flag)) {
      return of(undefined);
    }
    return this.languageService.translate<Array<{ countryCode?: string; name?: string }>>('countryCodes').pipe(
      map((countries: Array<{ countryCode?: string; name?: string }>) =>
        countries.find((country) => country.countryCode === flag.toUpperCase())
      ),
      safeMap(({ name }) => name),
      startWith(undefined as string)
    );
  }

  private marketingOffer(destination: string, tripType: TripType): Observable<MarketingOffer> {
    return this.cheapestPriceForAllDestinationsService.offerFor(destination, tripType).pipe(
      map((offer: MarketingOffer) => {
        return {
          ...offer,
          prices: offer.prices.filter(Boolean),
        };
      }),
      startWith(undefined as MarketingOffer)
    );
  }

  private setUpPrices(offer: BlockOfferData, marketingOffer: MarketingOffer, tripType: TripType): BlockOfferData {
    const priceOffer = marketingOffer?.prices.find((price) => price.travelClass === GlobalBookingTravelClass.ECONOMY);

    if (!priceOffer) {
      return offer;
    }

    const onewayPrice = tripType === TripType.ONEWAY ? priceOffer : undefined;
    const returnPrice = tripType === TripType.RETURN ? priceOffer : undefined;

    return { ...offer, onewayPrice, returnPrice };
  }

  onLinkClick([travelClass]: [GlobalBookingTravelClass, TripType]): void {
    this.destinationClicked.emit([this.offer, travelClass]);
    this.prefillBookingWidget(travelClass);
  }

  prefillBookingWidget(travelClass: GlobalBookingTravelClass, discountCode?: CMSDiscountCode): void {
    const offerDestination = snapshot(this.blockOffer$)?.destination;
    if (offerDestination) {
      this.store$.dispatch(GlobalBookingActions.setTripType({ tripType: this.tripType }));
      this.store$.dispatch(
        GlobalBookingActions.setTravelClass({
          travelClass: travelClass === GlobalBookingTravelClass.BUSINESS ? travelClass : GlobalBookingTravelClass.MIXED,
        })
      );

      if (discountCode?.code) {
        this.store$.dispatch(GlobalBookingActions.setDiscountCode({ discountCode: discountCode.code }));
      }

      this.gtmService.trackElement(
        `tile-${this.tripType.toLowerCase()}-${travelClass.toLowerCase()}`,
        GaContext.BLOCK_OFFER,
        ElementTypes.BANNER,
        `${offerDestination}`,
        ElementActions.CLICK
      );
    }
  }
}
